import React from 'react';
import "../../styles/Documentation.css";
import { Link, Typography } from '@mui/material';
import {ImageFigure, VideoFigure} from './utils/ContentFigure';
import CodeBlock from './utils/CodeBlock';
import fig1 from '../../images/web-site/fig1_quickstart.webp';
import fig2 from '../../images/web-site/fig2_simple.webp';
import fig3 from '../../images/web-site/fig3_detailed.webp';
import fig4 from '../../images/web-site/fig4_analyses.webp';
import vid1 from '../../images/web-site/vid1_simple_us_toc_sampledetail.mp4';
import vid2 from '../../images/web-site/vid2_simple_yukon_1000_chem.mp4';
import vid3 from '../../images/web-site/vid3_detail_search_beecher_iron.mp4';
import vid4 from '../../images/web-site/vid4_detail_sbc-1_al_methods.mp4';
import vid5 from '../../images/web-site/vid5_analyses_sbc-1_al_methods.mp4';
import vid6 from '../../images/web-site/vid6_simple_australia_map_sampledetail.mp4';

type ApiExampleProps = {
    children: string;
}
const ApiExample = ({ children }: ApiExampleProps) => (
    <>
        <Typography variant="body2" paragraph={false}>
            <Link href="/documentation/api-description">API</Link>:
        </Typography>
        <CodeBlock>{children}</CodeBlock>
    </>
);

const EX_SIMPLE_1 = `
{
  "type": "samples",
  "filters": {
    "is_shale": true,
    "country": [
      "United States"
    ]
  },
  "show": [
    "section_name",
    "height_meters",
    "site_type",
    "country",
    "basin_type",
    "meta_bin",
    "strat_unit",
    "environment_bin",
    "geol_age",
    "lithology_name",
    "interpreted_age",
    "min_age",
    "max_age",
    "toc"
  ]
}`;

const EX_SIMPLE_2 = `
{
  "type": "samples",
  "filters": {
    "interpreted_age": [
      0,
      1000
    ],
    "state_province": [
      "Yukon"
    ]
  },
  "show": [
    "section_name",
    "height_meters",
    "site_type",
    "country",
    "basin_type",
    "meta_bin",
    "strat_unit",
    "environment_bin",
    "geol_age",
    "lithology_name",
    "interpreted_age",
    "min_age",
    "max_age",
    "fe_carb_seq",
    "fe_mag_seq",
    "fe",
    "fe_hr_fe_t",
    "fe_ox_seq",
    "fe_py",
    "fe_hr",
    "fe_py_fe_hr",
    "toc",
    "alu",
    "mo",
    "u"
  ]
}`;

const EX_DETAIL_1 = `
{
  "type": "samples",
  "filters": {
    "data_source": [
      "SGP"
    ],
    "state_province": [
      "New York"
    ],
    "strat_unit": [
      "Frankfort Formation"
    ],
    "fe_hr_fe_t": [
      0.38,
      null
    ]
  },
  "show": [
    "fe_carb_seq",
    "fe_mag_seq",
    "fe_ox_seq",
    "fe_py",
    "fe_hr_fe_t",
    "fe",
    "toc",
    "delta_s34_py",
    "section_name",
    "basin_name",
    "basin_type",
    "strat_unit",
    "lithology_name",
    "geol_age",
    "fe_py_fe_hr",
    "analytical_method_code"
  ]
}`;

const EX_DETAIL_2 = `
{
  "type": "samples",
  "filters": {
    "original_name": [
      "SBC-1"
    ]
  },
  "show": [
    "alu",
    "geochem_method",
    "experiment_method_code",
    "analytical_method_code"
  ]
}`;

const EX_ANALYSES_1 = `
{
  "type": "analyses",
  "filters": {
    "original_name": [
      "SBC-1"
    ],
    "ana_code": [
      "Al",
      "Al2O3"
    ]
  },
  "show": [
    "lab",
    "batch_lab_id",
    "geochem_method",
    "experiment_method_code",
    "analytical_method_code",
    "upper_detection",
    "lower_detection",
    "analysis_ref_short"
  ]
}`;

const WebSite = () => (
    <div className="documentation-page">
        <Typography variant='h4' className="documentation-contents-header">The Search Website</Typography>
        <div className="web-site">
            <Typography variant='h5' gutterBottom >Understanding the Data</Typography>
            <Typography variant="body2" paragraph>
                To use the database most effectively, we recommend becoming familiar with the types and volume
                of data currently available. Descriptions of data, metadata, and data sources are provided on
                the <Link href="https://github.com/sgp-docs/sgp_phase2/wiki" target="_blank">SGP Phase 2 wiki</Link>
            </Typography>
            <Typography variant="body2" paragraph>
                The availability of metadata varies, in particular between data sources. As a result, applying
                filters based on certain fields (e.g. depositional environment, metamorphic bin or basin type)
                may exclude relevant samples if the information was not recorded for those records. Therefore,
                we recommend starting with broader searches and gradually adding filters to help ensure that
                potentially useful data are not overlooked.
            </Typography>

            <Typography variant='h5' gutterBottom >Getting Started</Typography>
            <ImageFigure
                src={fig1}
                alt="Search pane (top) and data table view (bottom)"
                caption={<><b>Fig.1:</b> Search pane (top) and data table view (bottom)</>}
            />

            <Typography variant='h5' gutterBottom >Simple Search</Typography>
            <Typography variant="body2" paragraph>
                Simple search is designed to provide quick access to data, by filtering only on high-level
                categories (age, country, state/province), and pre-selecting some standard sample context
                fields.
            </Typography>
            <Typography variant="body2" paragraph>
                The simple search averages all measurements for a given sample, converts oxides to elements
                (e.g. Al2O3 -&gt; Al), and standardizes units. It is not possible to sort/search by geochemical
                methodology.
            </Typography>
            <ImageFigure
                src={fig2}
                alt="Simple Search panel"
                caption={<><b>Fig.2:</b> Simple Search panel</>}
            />
            <Typography variant="body2" paragraph>
                Users can choose between <b>all samples</b>, <b>shales only</b> or <b>carbonates only</b> (Fig.2A).
                The latter categories are based on lithology first, and chemical composition second as follows:
            </Typography>
            <Typography variant="body2" paragraph>
                <ul>
                    <li>
                        <b>Shales</b>: Returns all 'fine-grained siliciclastic' samples (not specifically true
                        shales). This includes argillite, clay, claystone, marl, marlstone, meta-argillite,
                        metapelite, metasiltstone, mud, mudstone, oil shale, pelite, silt, siltite, siltstone
                        and shale. It also includes samples with no coded lithology but with total aluminum {'>'} 3.4
                        wt% (within one standard deviation of the average for shale + mudstone samples).
                    </li>
                    <li>
                        <b>Carbonates</b>: Returns all carbonate samples. This includes lime mudstone,
                        wackestone, packstone, grainstone, boundstone, crystalline limestone, dolomudstone,
                        dolowackestone, dolopackstone, dolograinstone, crystalline dolomite, ooze, carbonate,
                        limestone, dolomite, dolomicrite, limestone/dolomite. It also includes samples with no
                        coded lithology but with total combined calcium and magnesium {'>'} 15.3 wt% (within one
                        standard deviation of the average for true carbonate lithologies).
                    </li>
                </ul>
            </Typography>
            <Typography variant="body2" paragraph>
                Numerical range fields can be used to filter by <b>age range</b> in millions of years (Ma)
                (Fig.2B). We recommend starting with slightly broader time ranges than formal geological time
                bins if you want to capture samples that fall on or near interval boundaries. See wiki <Link href="https://github.com/sgp-docs/sgp_phase2/wiki/A.-Database-description#interpreted-age" target="_blank">Interpreted Age</Link> section,
                for a description of how numerical ages are assigned in SGP. Geological age names (e.g. Cambrian)
                are recorded separately so differences can occasionally arise depending on how and when
                interpreted ages were assigned.
            </Typography>
            <Typography variant="body2" paragraph>
                <b>Collection Site</b> (Fig.2C) filters by country and state/province. It is possible to choose
                multiple values for each category. The choice of country will influence which state/provinces
                show up in that dropdown.
            </Typography>
            <Typography variant="body2" paragraph>
                Under <b>Show</b> (Fig.2D) use the checkboxes to choose which types of geochemical data you want
                to include. The resulting data table will include samples that have results for <b><i>one or
                    more</i></b> of the chosen analytes.
            </Typography>
            <Typography variant="body2" paragraph>
                Under <b>Show {'>'} Samples Context</b> (Fig.2E) boxes have been pre-checked, including basic
                geological and geographical context such as site type, stratigraphy and lithology. However, it
                is also possible to remove and add fields as preferred.
            </Typography>
            <Typography variant="body2" paragraph>
                Finally, <b>Show {'>'} References</b> can be used to include DOIs, short or full citations for
                published data. When analyses from the same sample have been averaged from data appearing across
                multiple papers, the reference column will list all relevant sources in a concatenated list.
                (Including references may increase search time.)
            </Typography>

            <Typography variant='h6' gutterBottom>Simple Search Examples</Typography>
            <Typography variant='subtitle1' gutterBottom style={{ marginTop: 12, fontWeight: 'bold' }}>Example 1: United States shales with TOC</Typography>
            <Typography variant="body2" paragraph>
                Search for shale samples only, from the United States, which have total organic carbon values.
            </Typography>
            <VideoFigure src={vid1} />
            <ApiExample>{EX_SIMPLE_1}</ApiExample>
            <Typography variant='subtitle1' gutterBottom style={{ marginTop: 12, fontWeight: 'bold' }}>
                Example 2: Yukon samples {'>'} 1000Ma with iron speciation, TOC, and/or selected elemental data.
            </Typography>
            <Typography variant="body2" paragraph>
                Search for all samples from Yukon, Canada, from 1000Ma onwards, which have values for one or
                more of the following: Fe-carb, Fe-mag, Fe-ox, Fe-py, Fe, FeHR, Fe-py/FeHR, FeHR/FeT, TOC, Al,
                Mo, U.
            </Typography>
            <VideoFigure src={vid2} />
            <ApiExample>{EX_SIMPLE_2}</ApiExample>

            <Typography variant='h5' gutterBottom >Detailed Search</Typography>
            <Typography variant="body2" paragraph>
                The Detailed Search is designed to provide fine-level control, with the ability to filter based
                on all sample characteristics (<b>Sample Filters</b> Fig.3A), geochemical values and
                methodologies (<b>Analyte Filters</b> Fig.3B), and then choose exactly what information will
                appear in the table (<b>Show</b> Fig.3C).
            </Typography>
            <Typography variant="body2" paragraph>
                As with the Simple Search, this search also averages all measurements for a given sample,
                converts oxides to elements (e.g. Al2O3 -&gt; Al), and standardizes units.
            </Typography>
            <ImageFigure
                src={fig3}
                alt="Detailed Search panel"
                caption={<><b>Fig.3:</b> Detailed Search panel</>}
            />

            <Typography variant='h6' gutterBottom>Sample Filters</Typography>

            <Typography variant='subtitle1' gutterBottom style={{ marginTop: 12, fontWeight: 'bold' }}>Data Source/Project</Typography>
            <Typography variant="body2" paragraph>
                For details of the Data Sources refer to the <Link href="https://github.com/sgp-docs/sgp_phase2/wiki" target="_blank">wiki</Link>.
            </Typography>
            <Typography variant="body2" paragraph>
                <b>Projects</b> are used to group samples in various ways, for example by research theme or by
                publication. A single sample can belong to multiple projects. Filtering by project operates at
                the sample level - this means it will return samples that are associated with the chosen project
                and <i>any</i> associated analyses from those samples (depending on other applied filters).
            </Typography>
            <Typography variant="body2" paragraph>
                Project names that are based on a specific publication (e.g. Sperling et al. 2015_SA) will
                therefore return <b>samples</b> that were used in that study but the results could include <b>data</b> from
                other publications. To find out more precisely where individual results were published, use the
                Show {'>'} References option described below, or use the Analyses Search to find the data
                as-published (not averaged, no oxide conversions, and in the original units).
            </Typography>

            <Typography variant='subtitle1' gutterBottom style={{ marginTop: 12, fontWeight: 'bold' }}>Collection Site</Typography>
            <Typography variant="body2" paragraph>
                This allows for filtering based on the geography and regional geological context. For definitions
                and discussion see the <Link href="https://github.com/sgp-docs/sgp_phase2/wiki/A.-Database-description#geographical-context" target="_blank">wiki</Link>.
                Higher level geology is almost always coded, while basin, craton and metamorphic bin are variably
                available, depending mostly on the data source.
            </Typography>

            <Typography variant='subtitle1' gutterBottom style={{ marginTop: 12, fontWeight: 'bold' }}>Geological and Stratigraphic Context</Typography>
            <Typography variant="body2" paragraph>
                This allows for filtering based on stratigraphy and age (see <Link href="https://github.com/sgp-docs/sgp_phase2/wiki/A.-Database-description#geological-context" target="_blank">wiki</Link>).
            </Typography>
            <Typography variant="body2" paragraph>
                <b>Stratigraphic units</b> are <i>not</i> currently linked hierarchically in this menu, so in
                order to find all samples from a specific Group, for example, it would be necessary to select
                each relevant Member and Formation as well. We recommend simply using "Show" options below to
                include the stratigraphic unit names, unless you have very strict filter requirements. (We also
                recommend including "verbatim stratigraphic unit" in order to catch any author-coded details that
                could not be captured in the formal dictionary of stratigraphic names).
            </Typography>
            <Typography variant="body2" paragraph>
                <b>Depositional environments</b> are described <Link href="https://github.com/sgp-docs/sgp_phase2/wiki/A.-Database-description#depositional-environment" target="_blank">here</Link>.
            </Typography>
            <Typography variant="body2" paragraph>
                <b>Geological age</b> bins are from the <Link href="https://stratigraphy.org/chart/" target="_blank">International Commission on Stratigraphy</Link>.
                For any other provided names, or ages that cross boundaries, include "verbatim geological age" in
                the "Show" section.
            </Typography>

            <Typography variant='subtitle1' gutterBottom style={{ marginTop: 12, fontWeight: 'bold' }}>Lithology and Sample Type</Typography>
            <Typography variant="body2" paragraph>
                <b>Lithology Name</b> is the basic rock type (e.g. shale, limestone). <b>Lithology Texture</b> (e.g.
                silty, sandy) and <b>Lithology Composition</b> (e.g. calcareous, carbonaceous) are modifiers (and
                less commonly populated). Our lithological dictionary primarily comes from <Link href="https://macrostrat.org/api/defs/lithologies?all" target="_blank">Macrostrat</Link>,
                with some minor additional entries to accommodate diverse data from different sources. <b>Lithology
                    Class</b> is a higher-level grouping as defined in the Macrostrat system.
            </Typography>
            <Typography variant="body2" paragraph>
                Most samples in SGP have a bulk <b>sample type</b>, with the exception of some more recently added
                carbonates.
            </Typography>

            <Typography variant='subtitle1' gutterBottom style={{ marginTop: 12, fontWeight: 'bold' }}>Search by Original Name/Sample ID.</Typography>
            <Typography variant="body2" paragraph>
                This section allows the user to find a specific sample or samples. <b>Original Name</b> (original_num
                in the database) is the sample name as published or provided in a template. Original Name is not
                necessarily unique - for example, there are six different samples named "0", from studies where
                authors were using height in the section or depth in the core as the sole identifier.
            </Typography>
            <Typography variant="body2" paragraph>
                <b>Sample ID</b> (sample_id in the database) is an auto-generated integer identifier which is
                unique within the SGP database.
            </Typography>

            <Typography variant='h6' gutterBottom>Analyte Filters</Typography>
            <Typography variant="body2" paragraph>
                Within this section it is possible to filter data by measurement values, e.g. only show samples
                with Carbonate (wt %) {'>'} 80, or TOC between 0 and 0.5 wt%. Analytes are grouped into
                categories. In some rare cases the same analyte will appear in more than one category (e.g. N,
                Fe, S can be found in their relevant sections and in the more general "Elements").
            </Typography>

            <Typography variant='subtitle1' gutterBottom style={{ marginTop: 12, fontWeight: 'bold' }}>Methodology</Typography>
            <Typography variant="body2" paragraph>
                The <Link href="https://github.com/sgp-docs/sgp_phase2/wiki/C.-Analyses" target="_blank">Analyses</Link> section
                of the wiki provides descriptions of these categories. Briefly, <b>Prep method</b> is the way in
                which the samples were prepared, and especially the material used (e.g. powdered with agate or
                tungsten carbide or steel), <b>Experimental Method</b> describes steps undertaken before analysis
                such as acid treatments, and <b>Analytical Methods</b> are the way in which the data was measured
                (e.g. XRF or ICP-MS).
            </Typography>
            <Typography variant="body2" paragraph>
                <b>Code</b> and <b>Translation</b> (terminology from the British Geological Survey data model) are
                from the same dictionary, with Translation being a more detailed and descriptive version than the
                Code. If you are interested in filtering by multiple method types, pick <i>either</i> Code or
                Translation, and add your choices to the <i>same line</i>. Categories are not hierarchical, so
                choosing "ICP" as an Analytical Method Code, for example, will not automatically include ICP:MS,
                ICP:ES etc. - each version needs to be selected independently (ICP represents analyses where that
                was the only information available). To quickly select multiple related values, however, it is
                possible to search for a common term (e.g. ICP) and a "select all" option will appear in the
                list.
            </Typography>
            <Typography variant="body2" paragraph>
                The complexity of geochemical methodologies that have been performed in different labs, and that
                SGP has tried to meaningfully capture in the database, means there are a large number of
                different Codes/Translations. In turn, this means that actively selecting all variants of a
                Code/Translation to achieve the desired search result can be complicated. Searches also become
                complicated if trying to apply methodological filters across different and varied analytes.
                Please contact SGP leadership if you would like more help in applying methodological filters.
            </Typography>

            <Typography variant='h6' gutterBottom>Show</Typography>
            <Typography variant="body2" paragraph>
                After filtering, this section allows the user to choose which columns will appear in the table.
            </Typography>
            <Typography variant="body2" paragraph>
                <b>Filtering a column does not automatically display it. To include it in the results, you must
                    also select it here in the Show section</b>
            </Typography>
            <Typography variant="body2" paragraph>
                This section also includes an option to include concatenated <b>References</b> for the analyses.
            </Typography>
            <Typography variant="body2" paragraph>
                Any methodological details chosen under Show will appear as a drop down to the left of the row on
                the Data Table, and in columns in downloaded datasheets (see examples below).
            </Typography>

            <Typography variant='h6' gutterBottom>Detailed Search Examples</Typography>
            <Typography variant='subtitle1' gutterBottom style={{ marginTop: 12, fontWeight: 'bold' }}>
                Example 1: SGP samples, New York, Frankfort Formation, FeHR/FeT {'>'} 0.38.
            </Typography>
            <Typography variant="body2" paragraph>
                Search for samples provided by <i>SGP</i> collaborators, from <i>New York</i> State, from
                the <i>Frankfort Formation</i> with <i>FeHR/FeT</i> values <i>greater than 0.38</i>. Include
                columns for FeHR/FeT, Fe-carb, Fe-ox, Fe-mag, Fe-py, Fe, δ34S-py and TOC values, section name,
                basin name, basin type, stratigraphic unit, lithology name, geologic age bin, and analytical
                methods.
            </Typography>
            <VideoFigure src={vid3} />
            <ApiExample>{EX_DETAIL_1}</ApiExample>
            <Typography variant='subtitle1' gutterBottom style={{ marginTop: 12, fontWeight: 'bold' }}>Example 2: Aluminum from USGS standard SBC-1</Typography>
            <Typography variant="body2" paragraph>
                Search for aluminum values for SBC-1 sample. This example uses the USGS standard <Link href="https://www.usgs.gov/media/files/sbc-1-geochemical-reference-material-information-sheet" target="_blank">SBC-1</Link>,
                which has been analyzed many times, to demonstrate how methods are reported, values averaged,
                units standardized and oxides converted - compare with the Analyses Search example below, which
                is a corresponding version showing each analysis in a separate row.
            </Typography>
            <VideoFigure src={vid4} />
            <ApiExample>{EX_DETAIL_2}</ApiExample>

            <Typography variant='h5' gutterBottom >Analyses Search</Typography>
            <Typography variant="body2" paragraph>
                This search is designed for exploring individual geochemical results, as originally
                published/provided – this search does <i>not</i> average results for a sample, convert oxides to
                elements (e.g. Al and Al2O3 are both present), or standardize units.
            </Typography>
            <Typography variant="body2" paragraph>
                Unlike simple and detailed searches where each analyte (and associated unit) is a column header,
                in this kind of search, analytes and results are row-level information, under the column headers
                "analyte code", "analyte abundance" and "unit" (i.e. long vs. wide format).
            </Typography>
            <Typography variant="body2" paragraph>
                This search also provides access to additional lab- and analysis-specific details, under both <b>Analyte
                    Filters {'>'} Methodology</b> and <b>Show {'>'} Methodology</b>. Information about who ran and
                provided analyses is variably populated. Upper and lower detection limits are mostly available
                for data from commercial labs (e.g. Bureau Veritas or SGS). See wiki section <Link href="https://github.com/sgp-docs/sgp_phase2/wiki/C.-Analyses" target="_blank">Analyses</Link> and
                the section above for more details on methods.
            </Typography>
            <ImageFigure
                src={fig4}
                alt="Analyses Search panel"
                caption={<><b>Fig.4:</b> Analyses Search panel</>}
            />

            <Typography variant='h6' gutterBottom>Analyses Search Examples</Typography>
            <Typography variant='subtitle1' gutterBottom style={{ marginTop: 12, fontWeight: 'bold' }}>Example 1: Aluminum from USGS standard SBC-1</Typography>
            <Typography variant="body2" paragraph>
                Search for the USGS standard SBC-1, for all values of Al and Al2O3, and show the methodological
                details, including the lab where analyses were run, any external lab dataset identifiers, the
                preparation method, the experimental method and the analytical method, upper and lower detection
                limits and the short citation for where the data were published.
            </Typography>
            <VideoFigure src={vid5} />
            <ApiExample>{EX_ANALYSES_1}</ApiExample>

            <Typography variant='h5' gutterBottom >Map</Typography>
            <Typography variant="body2" paragraph>
                Samples from any search can be viewed on a map ("Map" in top right toolbar). They are color-coded
                based on geological time bins. Samples are grouped together based on the zoom level. Clicking on
                a group will bring up a list of samples, and individual sample details can be viewed in a
                dropdown. "Print Map" on the right will produce a PNG based on your current view.
            </Typography>
            <VideoFigure src={vid6} caption="Map view of selected samples from Australia." />

            <Typography variant='h5' gutterBottom >Exporting Data</Typography>
            <Typography variant="body2" paragraph>
                Once you have refined your search, simply click "Download Data" in the top right toolbar to
                export a CSV file.
            </Typography>
        </div>
    </div>
);

export default WebSite;
